/**
 * 调试工具：在页面底部创建一个可交互、可调整大小的胶卷视图
 */
export class ExportDebugger {
    private container: HTMLDivElement;
    private handle: HTMLDivElement;
    private static instance: ExportDebugger;

    // 状态变量，用于拖拽和收起功能
    private isDragging = false;
    private lastOpenHeight = 250; // 存储收起前的高度
    private readonly minHeight = 10; // 收起后的最小高度 (px)

    constructor() {
        this.container = document.createElement('div');
        Object.assign(this.container.style, {
            position: 'fixed',
            bottom: '0',
            left: '0',
            width: '100%',
            height: `${this.lastOpenHeight}px`, // 初始高度
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: '99999',
            overflow: 'hidden', // 隐藏滚动条，内容区自己滚动
            display: 'flex',
            flexDirection: 'column',
            borderTop: '3px solid #00ff00',
            boxSizing: 'border-box',
            color: 'white',
            fontFamily: 'monospace',
        });

        // 1. 创建可拖拽的顶部把手 (Handle)
        this.handle = document.createElement('div');
        this.handle.innerHTML = `
            <span style="color:#0f0; margin-right: 15px;">● 渲染调试模式</span>
            <span style="color:#888; font-size:11px;">(双击收起/展开 | 拖拽调整大小 | 点击图片下载)</span>
        `;
        Object.assign(this.handle.style, {
            width: '100%',
            height: `${this.minHeight}px`,
            lineHeight: `${this.minHeight}px`,
            padding: '0 10px',
            backgroundColor: 'rgba(30,30,30,0.9)',
            cursor: 'ns-resize', // 上下拖拽的光标样式
            userSelect: 'none', // 防止拖拽时选中文本
            boxSizing: 'border-box',
            flexShrink: '0', // 防止把手被压缩
        });

        // 2. 创建用于显示帧内容的滚动区域
        const contentArea = document.createElement('div');
        Object.assign(contentArea.style, {
            flexGrow: '1',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            padding: '10px',
        });
        
        // 将把手和内容区添加到主容器
        this.container.appendChild(this.handle);
        this.container.appendChild(contentArea);
        
        document.body.appendChild(this.container);

        // 绑定事件处理器，确保 'this' 指向正确
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onDoubleClick = this.onDoubleClick.bind(this);

        // 监听事件
        this.handle.addEventListener('mousedown', this.onMouseDown);
        this.handle.addEventListener('dblclick', this.onDoubleClick);
    }

    public static get(): ExportDebugger {
        if (!ExportDebugger.instance) ExportDebugger.instance = new ExportDebugger();
        return ExportDebugger.instance;
    }

    // --- 拖拽和收起功能的事件处理器 ---

    private onMouseDown(event: MouseEvent): void {
        event.preventDefault();
        this.isDragging = true;
        document.body.addEventListener('mousemove', this.onMouseMove);
        document.body.addEventListener('mouseup', this.onMouseUp);
    }

    private onMouseMove(event: MouseEvent): void {
        if (!this.isDragging) return;
        
        // 根据鼠标位置计算新的高度
        const newHeight = window.innerHeight - event.clientY;

        // 限制最大和最小高度
        const maxHeight = window.innerHeight * 0.9; // 最多占屏幕90%
        if (newHeight > this.minHeight && newHeight < maxHeight) {
            this.container.style.height = `${newHeight}px`;
        }
    }

    private onMouseUp(): void {
        if (!this.isDragging) return;
        this.isDragging = false;
        document.body.removeEventListener('mousemove', this.onMouseMove);
        document.body.removeEventListener('mouseup', this.onMouseUp);
        
        // 如果当前不是收起状态，则记录当前高度
        const currentHeight = this.container.offsetHeight;
        if (currentHeight > this.minHeight + 10) {
            this.lastOpenHeight = currentHeight;
        }
    }

    private onDoubleClick(): void {
        const currentHeight = this.container.offsetHeight;
        if (currentHeight > this.minHeight + 10) {
            // 当前是展开状态 -> 收起
            this.lastOpenHeight = currentHeight; // 保存当前高度
            this.container.style.transition = 'height 0.2s ease-out';
            this.container.style.height = `${this.minHeight}px`;
        } else {
            // 当前是收起状态 -> 展开
            this.container.style.transition = 'height 0.2s ease-out';
            this.container.style.height = `${this.lastOpenHeight}px`;
        }
        // 动画结束后移除 transition，避免影响拖拽
        setTimeout(() => this.container.style.transition = '', 200);
    }

    // --- 核心功能方法 (几乎无变化) ---

    public logFrame(index: number, rawCanvas: HTMLCanvasElement, aiDataUrl: string, logInfo: string, isSuspicious: boolean) {
        const contentArea = this.container.querySelector('div:last-child') as HTMLDivElement;
        if (!contentArea) return;
        
        const item = document.createElement('div');
        Object.assign(item.style, {
            display: 'inline-block',
            marginRight: '10px',
            verticalAlign: 'top',
            border: isSuspicious ? '2px solid red' : '1px solid #444',
            padding: '5px',
            background: isSuspicious ? 'rgba(255,0,0,0.2)' : 'transparent'
        });

        const time = new Date().toISOString().split('T')[1].replace('Z','');
        const rawThumb = rawCanvas.toDataURL('image/jpeg', 0.5);
        
        item.innerHTML = `
            <div style="font-size:10px; margin-bottom:5px;">
                <b>#${index}</b> [${time}]<br/>
                ${isSuspicious ? '<b style="color:red">⚠️ 空白/异常</b>' : '<span style="color:#aaa">正常</span>'}
            </div>
            <div style="display:flex; gap:5px;">
                <div style="text-align:center">
                    <img src="${rawThumb}" style="height:120px; border:1px solid #666; cursor:pointer;" title="原始3D帧 (点击下载)" onclick="this.parentElement.nextElementSibling.click()">
                    <div style="font-size:9px; color:#888">Input</div>
                </div>
                <div style="text-align:center">
                    <img src="${aiDataUrl}" style="height:120px; border:1px solid #666; cursor:pointer;" title="AI线稿帧 (点击下载)" onclick="this.parentElement.nextElementSibling.nextElementSibling.click()">
                    <div style="font-size:9px; color:#888">Output</div>
                </div>
                <a href="${rawThumb}" download="frame_${index}_raw.jpg" style="display:none"></a>
                <a href="${aiDataUrl}" download="frame_${index}_ai.png" style="display:none"></a>
            </div>
            <div style="font-size:9px; color:#bbb; margin-top:5px; max-width: 200px; white-space: normal;">
                ${logInfo}
            </div>
        `;

        // 插入到开头
        contentArea.insertBefore(item, contentArea.firstChild);

        // 限制显示最近50帧
        if (contentArea.children.length > 50) {
            contentArea.lastElementChild?.remove();
        }
    }

    public isCanvasBlank(canvas: HTMLCanvasElement): boolean {
        // 这个方法保持不变
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return true;
        const pixelBuffer = new Uint32Array(ctx.getImageData(0, 0, 1, 1).data.buffer);
        return !pixelBuffer[0];
    }
}